import React, { useCallback, useEffect, useState } from 'react';
import { Mail, CalendarCheck, Users } from 'lucide-react';
import { adminGetMessages } from '../../lib/adminApi';

const SubTab = ({ active, onClick, icon: Icon, label, count }) => (
  <button onClick={onClick} className={`inline-flex items-center gap-2 px-4 py-2.5 rounded font-600 text-sm transition-colors ${active ? 'bg-[#17565f] text-white' : 'bg-white border border-[#e4ddd0] text-[#5a6b71] hover:text-[#0f2e38]'}`}>
    <Icon className="h-4 w-4" /> {label} <span className={`text-xs ${active ? 'text-white/70' : 'text-[#8a9599]'}`}>({count})</span>
  </button>
);

const MessagesManager = () => {
  const [data, setData] = useState({ contacts: [], visits: [], subscribers: [] });
  const [sub, setSub] = useState('visits');
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      setData(await adminGetMessages());
    } catch (err) {
      console.error('Failed to load messages:', err);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  if (loading) return <p className="text-[#5a6b71]">Loading…</p>;

  return (
    <div>
      <h2 className="font-serif-display text-3xl text-[#0f2e38] mb-1">Inbox</h2>
      <p className="text-[#5a6b71] mb-6">Visit requests, contact messages, and newsletter subscribers.</p>
      <div className="flex flex-wrap gap-3 mb-6">
        <SubTab active={sub === 'visits'} onClick={() => setSub('visits')} icon={CalendarCheck} label="Visit Requests" count={data.visits.length} />
        <SubTab active={sub === 'contacts'} onClick={() => setSub('contacts')} icon={Mail} label="Messages" count={data.contacts.length} />
        <SubTab active={sub === 'subscribers'} onClick={() => setSub('subscribers')} icon={Users} label="Subscribers" count={data.subscribers.length} />
      </div>

      {sub === 'visits' && (
        <div className="grid gap-3">
          {data.visits.length === 0 && <p className="text-[#5a6b71]">No visit requests yet.</p>}
          {data.visits.map((v) => (
            <div key={v.id} className="bg-white border border-[#e4ddd0] rounded-lg p-4">
              <div className="flex justify-between gap-4 flex-wrap">
                <p className="font-600 text-[#0f2e38]">{v.name} <span className="font-400 text-[#5a6b71]">· {v.people} coming</span></p>
                <p className="text-sm text-[#8a9599]">{v.visitDate || '—'}</p>
              </div>
              <p className="text-sm text-[#5a6b71] mt-1">{v.email} · {v.phone || 'no phone'}</p>
              {v.message && <p className="text-[#3a4d54] mt-2">{v.message}</p>}
            </div>
          ))}
        </div>
      )}

      {sub === 'contacts' && (
        <div className="grid gap-3">
          {data.contacts.length === 0 && <p className="text-[#5a6b71]">No messages yet.</p>}
          {data.contacts.map((c) => (
            <div key={c.id} className="bg-white border border-[#e4ddd0] rounded-lg p-4">
              <p className="font-600 text-[#0f2e38]">{c.name}</p>
              <p className="text-sm text-[#5a6b71] mt-1">{c.email} · {c.phone || 'no phone'}</p>
              <p className="text-[#3a4d54] mt-2">{c.message}</p>
            </div>
          ))}
        </div>
      )}

      {sub === 'subscribers' && (
        <div className="bg-white border border-[#e4ddd0] rounded-lg p-4">
          {data.subscribers.length === 0 && <p className="text-[#5a6b71]">No subscribers yet.</p>}
          <div className="grid sm:grid-cols-2 gap-2">
            {data.subscribers.map((s) => (
              <p key={s.id} className="text-[#3a4d54] text-sm py-1 border-b border-[#f0ebe0]">{s.email}</p>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

export default MessagesManager;
